import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { Building2, MapPin, User, Mail, Phone, Lock, Loader2, ChevronRight, Info, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { WILAYAS } from '../data/wilayas';

/**
 * Page d'inscription unifiée (style AERA-Online) — un seul formulaire pour tous :
 * dentistes, laboratoires, chirurgiens, orthodontistes, fabricants, fournisseurs, étudiants.
 * Le `organization_type` détermine automatiquement le rôle backend (buyer/supplier).
 */

const ORG_TYPES = [
  { value: 'general_dentistry', label: 'Cabinet dentaire — Médecine générale', role: 'buyer' },
  { value: 'lab',               label: 'Laboratoire de prothèse',              role: 'buyer' },
  { value: 'oral_surgery',      label: 'Chirurgie buccale',                    role: 'buyer' },
  { value: 'maxillofacial',     label: 'Chirurgie maxillo-faciale',            role: 'buyer' },
  { value: 'orthodontics',      label: 'Orthodontie',                          role: 'buyer' },
  { value: 'hygienist',         label: 'Hygiéniste / Prophylaxie',             role: 'buyer' },
  { value: 'student',           label: 'Étudiant en médecine dentaire',        role: 'buyer' },
  { value: 'manufacturer',      label: 'Fabricant',                            role: 'supplier' },
  { value: 'supplier',          label: 'Fournisseur / Distributeur',           role: 'supplier' },
  { value: 'reparateur',        label: 'Technicien / Réparateur matériel',     role: 'reparateur' },
  { value: 'other',             label: 'Autre',                                role: 'buyer' },
];

const Register = () => {
  useDocumentMeta({
    title: 'Inscription — Comptoir-Online',
    description: "Créez votre compte Comptoir-Online en 1 minute. Dentistes, labos, chirurgiens, fournisseurs — tous bienvenus.",
  });
  const { register } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [form, setForm] = useState({
    organization_type: 'general_dentistry',
    company: '', name: '',
    address: '', city: '', commune: '',
    email: '', phone: '', password: '',
  });
  const [errors, setErrors] = useState({});
  React.useEffect(() => {
    const role = searchParams.get('role');
    if (role === 'supplier') setForm(f => ({...f, organization_type: 'supplier'}));
    if (role === 'reparateur') setForm(f => ({...f, organization_type: 'reparateur'}));
  }, []);
  const [busy, setBusy] = useState(false);

  const set = (f, v) => {
    setForm(prev => ({ ...prev, [f]: v }));
    setErrors(e => ({ ...e, [f]: undefined }));
  };

  const orgMeta = ORG_TYPES.find(o => o.value === form.organization_type) || ORG_TYPES[0];
  const isSupplier = orgMeta.role === 'supplier';

  const validate = () => {
    const e = {};
    if (!form.name.trim())     e.name     = 'Nom requis';
    if (!form.company.trim())  e.company  = isSupplier ? 'Nom de société requis' : 'Nom du cabinet/labo requis';
    if (!form.city)            e.city     = 'Wilaya requise';
    if (!form.address.trim())  e.address  = 'Adresse requise';
    if (!form.phone.trim())    e.phone    = 'Téléphone requis';
    if (!form.email.trim())    e.email    = 'Email requis';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email invalide';
    if (!form.password)        e.password = 'Mot de passe requis';
    else if (form.password.length < 6) e.password = 'Minimum 6 caractères';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const newUser = await register({
        organization_type: form.organization_type,
        name:    form.name,
        company: form.company,
        email:   form.email,
        password: form.password,
        address: form.address,
        city:    form.city,
        commune: form.commune,
        phone:   form.phone,
      });
      if (isSupplier) {
        if (newUser?.approved) {
          toast({ title: '✅ Compte fournisseur créé', description: 'Accès immédiat à votre tableau de bord.' });
          navigate('/fournisseur');
        } else {
          toast({ title: '✅ Demande envoyée', description: 'Votre compte sera activé après validation par notre équipe.' });
          navigate('/');
        }
      } else {
        toast({ title: '✅ Compte créé', description: 'Bienvenue chez Comptoir-Online !' });
        navigate('/');
      }
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Inscription impossible' });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] py-6 sm:py-10 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-[#1F4D3F]">Créer un compte</h1>
          <p className="text-sm text-[#7A6F5C] mt-2">
            Une seule inscription, simple et rapide — pour dentistes, laboratoires, chirurgiens, étudiants ou fournisseurs.
          </p>
        </div>

        <form onSubmit={submit} data-testid="register-form" className="bg-white border border-[#E8E0D0] rounded-2xl p-5 sm:p-8 space-y-5 shadow-sm">

          {/* Organization type */}
          <div>
            <label className="text-sm font-medium text-[#2D2A26] mb-1.5 flex items-center gap-1.5">
              <Building2 className="w-4 h-4 text-[#1F4D3F]" /> Type d'organisation *
            </label>
            <select
              data-testid="register-org-type"
              value={form.organization_type}
              onChange={(e) => set('organization_type', e.target.value)}
              className="w-full h-11 px-3 rounded-md border border-[#E8E0D0] bg-white text-[#2D2A26] focus:outline-none focus:ring-2 focus:ring-[#1F4D3F] text-base">
              {ORG_TYPES.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <p className="text-[11px] text-[#7A6F5C] mt-1 flex items-start gap-1">
              <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
              {isSupplier
                ? 'Vous serez identifié comme fournisseur — accès au tableau de bord catalogue.'
                : 'Vous serez identifié comme acheteur professionnel — accès au comparateur et aux commandes.'}
            </p>
          </div>

          {/* Organization name + contact name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#2D2A26] mb-1.5 block">
                {isSupplier ? 'Nom de la société *' : 'Nom du cabinet / labo *'}
              </label>
              <Input data-testid="register-company"
                value={form.company} onChange={(e) => set('company', e.target.value)}
                placeholder={isSupplier ? 'Ex: DentalSupply DZ' : 'Ex: Cabinet Dr. Belkacem'}
                className="h-11 text-base" />
              {errors.company && <p className="text-[11px] text-rose-600 mt-1">{errors.company}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-[#2D2A26] mb-1.5 flex items-center gap-1.5">
                <User className="w-4 h-4 text-[#1F4D3F]" /> Nom du contact *
              </label>
              <Input data-testid="register-name"
                value={form.name} onChange={(e) => set('name', e.target.value)}
                placeholder="Prénom et nom"
                className="h-11 text-base" />
              {errors.name && <p className="text-[11px] text-rose-600 mt-1">{errors.name}</p>}
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="text-sm font-medium text-[#2D2A26] mb-1.5 flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-[#1F4D3F]" /> Adresse complète *
            </label>
            <Input data-testid="register-address"
              value={form.address} onChange={(e) => set('address', e.target.value)}
              placeholder="N°, rue, quartier"
              className="h-11 text-base" />
            {errors.address && <p className="text-[11px] text-rose-600 mt-1">{errors.address}</p>}
          </div>

          {/* Wilaya + Commune */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#2D2A26] mb-1.5 block">Wilaya *</label>
              <select
                data-testid="register-wilaya"
                value={form.city} onChange={(e) => set('city', e.target.value)}
                className="w-full h-11 px-3 rounded-md border border-[#E8E0D0] bg-white text-base focus:outline-none focus:ring-2 focus:ring-[#1F4D3F]">
                <option value="">Choisir une wilaya…</option>
                {WILAYAS.map(w => <option key={w} value={w}>{w}</option>)}
              </select>
              {errors.city && <p className="text-[11px] text-rose-600 mt-1">{errors.city}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-[#2D2A26] mb-1.5 block">Commune <span className="text-[#7A6F5C] text-xs">(optionnel)</span></label>
              <Input data-testid="register-commune"
                value={form.commune} onChange={(e) => set('commune', e.target.value)}
                placeholder="Ex: Bab Ezzouar"
                className="h-11 text-base" />
            </div>
          </div>

          {/* Same-day delivery hint */}
          {form.city && (
            <div className="bg-[#FFF8EC] border border-[#F0E0B8] rounded-lg p-3 text-xs text-[#7A6520] flex items-start gap-2" data-testid="register-wilaya-info">
              <Truck className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Des fournisseurs de <strong>{form.city}</strong> et des wilayas voisines peuvent livrer chez vous le jour même.</span>
            </div>
          )}

          {/* Email + Phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#2D2A26] mb-1.5 flex items-center gap-1.5">
                <Mail className="w-4 h-4 text-[#1F4D3F]" /> Email *
              </label>
              <Input data-testid="register-email" type="email"
                value={form.email} onChange={(e) => set('email', e.target.value)}
                placeholder="vous@exemple.com" autoComplete="email"
                className="h-11 text-base" />
              {errors.email && <p className="text-[11px] text-rose-600 mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-[#2D2A26] mb-1.5 flex items-center gap-1.5">
                <Phone className="w-4 h-4 text-[#1F4D3F]" /> Téléphone *
              </label>
              <Input data-testid="register-phone" type="tel"
                value={form.phone} onChange={(e) => set('phone', e.target.value)}
                placeholder="+213 5 55 00 00 00" autoComplete="tel"
                className="h-11 text-base" />
              {errors.phone && <p className="text-[11px] text-rose-600 mt-1">{errors.phone}</p>}
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="text-sm font-medium text-[#2D2A26] mb-1.5 flex items-center gap-1.5">
              <Lock className="w-4 h-4 text-[#1F4D3F]" /> Mot de passe *
            </label>
            <Input data-testid="register-password" type="password"
              value={form.password} onChange={(e) => set('password', e.target.value)}
              placeholder="Minimum 6 caractères" autoComplete="new-password"
              className="h-11 text-base" />
            {errors.password && <p className="text-[11px] text-rose-600 mt-1">{errors.password}</p>}
          </div>

          {/* Submit */}
          <Button type="submit" disabled={busy}
            data-testid="register-submit"
            className="w-full h-12 bg-[#1F4D3F] hover:bg-[#173B30] text-white text-base">
            {busy
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Création en cours…</>
              : <>Créer mon compte <ChevronRight className="w-4 h-4 ml-1" /></>}
          </Button>

          <div className="text-center text-sm text-[#7A6F5C]">
            Déjà un compte ?{' '}
            <Link to="/connexion" className="text-[#1F4D3F] font-medium hover:underline">Se connecter</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
